export type Severity = 'primary' | 'secondary' | 'success' | 'brown' | 'danger' | 'info' | 'disabled' | 'white' | 'danger-light';
export type Size = 'xs' | 'sm' | 'md';
export type ButtonBehaviour = 'static' | 'responsive';

interface SeverityClasses {
  bg: string;
  text: string;
  border: string;
  textSecondary: string;
  hover: [string, string, string];
}

export const severitySwitch = (severity: Severity): SeverityClasses => {
  switch (severity) {
    case 'primary':
      return {
        bg: 'bg-yellow-400',
        text: 'text-brown-600',
        border: 'border-yellow-400',
        textSecondary: 'text-yellow-400',
        hover: ['hover:bg-yellow-300', 'hover:text-brown-800', 'hover:bg-yellow-100'],
      };
    case 'secondary':
      return {
        bg: 'bg-yellow-200',
        text: 'text-brown-600',
        border: 'border-yellow-200',
        textSecondary: 'text-yellow-200',
        hover: ['hover:bg-yellow-300', 'hover:text-yellow-800', 'hover:bg-yellow-100'],
    };
    case 'success':
      return {
        bg: 'bg-green-600',
        text: 'text-white',
        border: 'border-green-600',
        textSecondary: 'text-green-600',
        hover: ['hover:bg-green-700', 'hover:text-green-700', 'hover:bg-green-100'],
      };
    case 'brown':
      return {
        bg: 'bg-brown-600',
        text: 'text-yellow-300',
        border: 'border-brown-600',
        textSecondary: 'text-brown-600',
        hover: ['hover:bg-brown-800', 'hover:text-brown-800', 'hover:bg-brown-100'],
      };
    case 'danger':
      return {
        bg: 'bg-red-500',
        text: 'text-white',
        border: 'border-red-500',
        textSecondary: 'text-red-500',
        hover: ['hover:bg-red-900', 'hover:text-red-900', 'hover:bg-red-100'],
      };
    case 'danger-light':
      return {
        bg: 'bg-white',
        text: 'text-red-500',
        border: 'border-white',
        textSecondary: 'text-red-500',
        hover: ['hover:bg-gray-50', 'hover:text-red-700', ''],
      };
    case 'info':
      return {
        bg: 'bg-blue-500',
        text: 'text-white',
        border: 'border-blue-500',
        textSecondary: 'text-blue-500',
        hover: ['hover:bg-blue-800', 'hover:text-blue-800', 'hover:bg-blue-100'],
      };
    case 'disabled':
      return {
        bg: 'bg-gray-50',
        text: 'text-gray-300',
        border: 'border-gray-50',
        textSecondary: 'text-gray-50',
        hover: ['', '', ''],
      };
    case 'white':
      return {
        bg: 'bg-white',
        text: 'text-gray-800',
        border: 'border-white',
        textSecondary: 'text-white',
        hover: ['hover:bg-gray-50', 'hover:text-gray-50', ''],
      };
    default:
      return {
        bg: 'bg-yellow-700',
        text: 'text-brown-600',
        border: 'border-yellow-700',
        textSecondary: 'text-yellow-700',
        hover: ['hover:bg-yellow-500', 'hover:text-brown-800', 'hover:bg-yellow-200'],
      };
  }
};

const SIZE_CLASSES: Record<ButtonBehaviour, Record<string, { label: string; sizing: string; icon: string; iconOnly: string }>> = {
  static: {
    md: { label: 'text-sm gap-2', sizing: 'min-h-11 py-2 px-5', icon: 'text-lg', iconOnly: 'w-11 h-11' },
    sm: { label: 'text-xs gap-1.5', sizing: 'min-h-10 py-1.5 px-4', icon: 'text-base', iconOnly: 'w-10 h-10' },
    xs: { label: 'text-2xs gap-1', sizing: 'min-h-8 py-1 px-2', icon: 'text-sm', iconOnly: 'w-8 h-8' },
  },
  responsive: {
    md: { label: 'text-2xs gap-1 lg:text-sm lg:gap-2', sizing: 'min-h-8 py-1 px-2 lg:min-h-11 lg:py-2 lg:px-5', icon: 'text-sm lg:text-lg', iconOnly: 'w-8 h-8 lg:w-11 lg:h-11' },
    sm: { label: 'text-xs gap-1.5', sizing: 'min-h-10 py-1.5 px-4', icon: 'text-base', iconOnly: 'w-10 h-10' },
    xs: { label: 'text-2xs gap-1', sizing: 'min-h-8 py-1 px-2', icon: 'text-sm', iconOnly: 'w-8 h-8' },
  }
};

const BUTTON_QUANTITY_CLASSES: Record<ButtonBehaviour, Record<string, { label: string; sizing: string; icon: string; }>> = {
  static: {
    md: { label: 'text-base w-12', sizing: 'w-9 h-9', icon: 'text-2xl' },
    sm: { label: 'text-sm w-10', sizing: 'w-8 h-8', icon: 'text-lg' },
    xs: { label: 'text-xs w-6', sizing: 'w-6 h-6', icon: 'text-base' },
  },
  responsive: {
    md: { label: 'text-xs w-6 lg:text-base lg:w-12', sizing: 'w-6 h-6 md:w-8 md:h-8 lg:w-9 lg:h-9', icon: 'text-base lg:text-2xl' },
    sm: { label: 'text-xs w-6 lg:text-sm lg:w-10', sizing: 'w-6 h-6 lg:w-8 lg:h-8', icon: 'text-base lg:text-lg' },
    xs: { label: 'text-xs w-6', sizing: 'w-6 h-6', icon: 'text-base' },
  }
}

export function getSizeSet(size: Size, behaviour: ButtonBehaviour = 'static') {
  return SIZE_CLASSES[behaviour][size];
}

export function getQuantitySizeSet(size: Size, behaviour: ButtonBehaviour = 'static') {
  return BUTTON_QUANTITY_CLASSES[behaviour][size];
}
